import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import { employeeApi } from '../../services/api';
import api from '../../services/api';
import { fmtDate, ROLE_LABELS, fmtPKR } from '../../utils/helpers';
import { Spinner, EmptyState, Modal, Pagination } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Search, Edit2, Key } from 'lucide-react';

// ── Employees ─────────────────────────────────────────────────────────────────
export function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editEmp, setEditEmp]   = useState(null);
  const [form, setForm] = useState({
    empCode:'', name:'', email:'', mobile:'', password:'',
    designation:'', department:'', role:'STAFF', employmentType:'FULL_TIME',
    basicSalary:'', joiningDate:'', nationalId:'',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeApi.getAll({ page, limit:30,
        search: search || undefined, role: roleFilter || undefined });
      if (res.success) { setEmployees(res.data); setPagination(res.pagination||{}); }
    } finally { setLoading(false); }
  }, [page, search, roleFilter]);

  useEffect(() => { const t=setTimeout(load,300); return ()=>clearTimeout(t); }, [load]);

  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const handleCreate = async () => {
    if (!form.empCode||!form.name||!form.email) { toast.error('Code, name & email required'); return; }
    try {
      const res = editEmp
        ? await employeeApi.update(editEmp.emp_id, form)
        : await employeeApi.create(form);
      if (res.success||res.message==='Employee updated') {
        toast.success(editEmp ? 'Employee updated' : 'Employee created');
        setShowCreate(false); setEditEmp(null);
        setForm({ empCode:'',name:'',email:'',mobile:'',password:'',designation:'',department:'',
          role:'STAFF',employmentType:'FULL_TIME',basicSalary:'',joiningDate:'',nationalId:'' });
        load();
      } else throw new Error(res.message);
    } catch(e) { toast.error(e.message||'Failed'); }
  };

  const openEdit = (emp) => {
    setEditEmp(emp);
    setForm({ empCode:emp.emp_code||'',name:emp.name||'',email:emp.email||'',mobile:emp.mobile||'',
      password:'',designation:emp.designation||'',department:emp.department||'',role:emp.role||'STAFF',
      employmentType:emp.employment_type||'FULL_TIME',basicSalary:emp.basic_salary||'',
      joiningDate:emp.joining_date ? emp.joining_date.slice(0,10) : '',nationalId:emp.national_id||'' });
    setShowCreate(true);
  };

  const resetPwd = async (empId) => {
    const pwd = prompt('New password (min 8 chars):');
    if (!pwd||pwd.length<8) { toast.error('Password too short'); return; }
    try {
      await employeeApi.resetPassword(empId, { newPassword:pwd });
      toast.success('Password reset');
    } catch { toast.error('Failed'); }
  };

  const toggleActive = async (emp) => {
    try {
      await employeeApi.update(emp.emp_id, { isActive: !emp.is_active });
      toast.success(`Employee ${emp.is_active ? 'deactivated' : 'activated'}`);
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <AppShell title="Employees" actions={
      <button className="btn btn-primary btn-sm" onClick={() => { setEditEmp(null); setShowCreate(true); }}>
        <Plus size={14}/> Add Employee
      </button>
    }>
      <div className="filters-bar">
        <div className="search-input">
          <Search size={14}/>
          <input placeholder="Search name, code, email…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <select className="form-control" style={{ width:160 }} value={roleFilter} onChange={e=>setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {Object.entries(ROLE_LABELS).filter(([k])=>!['B2C','B2B'].includes(k)).map(([v,l])=>
            <option key={v} value={v}>{l}</option>
          )}
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : employees.length ? (
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Role</th><th>Dept</th><th>Hub</th><th>Salary</th><th>Joined</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {employees.map(e=>(
                  <tr key={e.emp_id}>
                    <td><code style={{ fontSize:'.78rem', fontWeight:700 }}>{e.emp_code}</code></td>
                    <td>
                      <div style={{ fontWeight:500 }}>{e.name}</div>
                      <div className="text-sm text-muted">{e.email}</div>
                    </td>
                    <td><span className="badge badge-navy">{ROLE_LABELS[e.role]||e.role}</span></td>
                    <td className="text-sm">{e.department||'—'}</td>
                    <td className="text-sm">{e.hub_name||'—'}</td>
                    <td className="text-sm">{e.basic_salary ? fmtPKR(e.basic_salary) : '—'}</td>
                    <td className="text-sm">{e.joining_date ? fmtDate(e.joining_date) : '—'}</td>
                    <td><span className={`badge ${e.is_active?'badge-success':'badge-gray'}`}>{e.is_active?'Active':'Inactive'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-outline btn-sm" onClick={()=>openEdit(e)}><Edit2 size={12}/></button>
                        <button className="btn btn-outline btn-sm" onClick={()=>resetPwd(e.emp_id)}><Key size={12}/></button>
                        <button className={`btn btn-sm ${e.is_active?'btn-outline':'btn-success'}`} onClick={()=>toggleActive(e)} style={{ fontSize:'.7rem', padding:'3px 8px' }}>
                          {e.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="👥" title="No employees found" />}
        </div>
        <Pagination page={page} pages={pagination.pages||1} onPage={setPage} />
      </div>

      <Modal open={showCreate} onClose={()=>{setShowCreate(false);setEditEmp(null);}}
        title={editEmp ? `Edit: ${editEmp.name}` : 'Add Employee'} size="modal-lg">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Employee Code <span className="req">*</span></label>
              <input className="form-control" placeholder="EMP-002" value={form.empCode} onChange={set('empCode')} disabled={!!editEmp} />
            </div>
            <div className="form-group">
              <label className="form-label">Full Name <span className="req">*</span></label>
              <input className="form-control" value={form.name} onChange={set('name')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email <span className="req">*</span></label>
              <input className="form-control" type="email" value={form.email} onChange={set('email')} disabled={!!editEmp} />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile</label>
              <input className="form-control" value={form.mobile} onChange={set('mobile')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Role <span className="req">*</span></label>
              <select className="form-control" value={form.role} onChange={set('role')}>
                {Object.entries(ROLE_LABELS).filter(([k])=>!['B2C','B2B'].includes(k)).map(([v,l])=>
                  <option key={v} value={v}>{l}</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Employment Type</label>
              <select className="form-control" value={form.employmentType} onChange={set('employmentType')}>
                <option value="FULL_TIME">Full Time</option>
                <option value="PART_TIME">Part Time</option>
                <option value="CONTRACT">Contract</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Designation</label>
              <input className="form-control" value={form.designation} onChange={set('designation')} />
            </div>
            <div className="form-group">
              <label className="form-label">Department</label>
              <input className="form-control" value={form.department} onChange={set('department')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Basic Salary (PKR)</label>
              <input className="form-control" type="number" value={form.basicSalary} onChange={set('basicSalary')} />
            </div>
            <div className="form-group">
              <label className="form-label">Joining Date</label>
              <input className="form-control" type="date" value={form.joiningDate} onChange={set('joiningDate')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">CNIC / National ID</label>
              <input className="form-control" placeholder="35202-1234567-1" value={form.nationalId} onChange={set('nationalId')} />
            </div>
            {!editEmp && (
              <div className="form-group">
                <label className="form-label">Password <span style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>(default: CP@123456)</span></label>
                <input className="form-control" type="password" placeholder="Leave blank for default" value={form.password} onChange={set('password')} />
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>{setShowCreate(false);setEditEmp(null);}}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate}>{editEmp?'Save Changes':'Create Employee'}</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── Attendance ─────────────────────────────────────────────────────────────────
export function AttendancePage() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('summary');
  const [month, setMonth]     = useState(new Date().getMonth()+1);
  const [year, setYear]       = useState(new Date().getFullYear());
  const [showMark, setShowMark] = useState(false);
  const [markForm, setMarkForm] = useState({
    empId:'', date:new Date().toISOString().slice(0,10), checkIn:'', checkOut:'', status:'PRESENT', source:'MANUAL'
  });
  const [showAttEdit, setShowAttEdit] = useState(false);
  const [editAtt, setEditAtt]   = useState(null);
  const [attForm, setAttForm]   = useState({ status:'PRESENT', checkIn:'', checkOut:'', notes:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r,s] = await Promise.all([
        employeeApi.getAttendance({ month, year }),
        api.get(`/attendance/summary?month=${month}&year=${year}`).then(r=>r.data)
      ]);
      if (r.success) setRecords(r.data);
      if (s.success) setSummary(s.data);
    } catch { } finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const handleMark = async () => {
    try {
      await employeeApi.markAttendance(markForm);
      toast.success('Attendance recorded'); setShowMark(false); load();
    } catch { toast.error('Failed'); }
  };

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const saveAttEdit = async () => {
    try {
      await api.patch('/hr/attendance/'+editAtt.att_id, { status:attForm.status, checkIn:attForm.checkIn||undefined, checkOut:attForm.checkOut||undefined, notes:attForm.notes||undefined }).then(r=>r.data);
      toast.success('Attendance updated'); setShowAttEdit(false); setEditAtt(null); load();
    } catch(e){ toast.error(e.response?.data?.message||'Failed'); }
  };

  return (
    <AppShell title="Attendance" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowMark(true)}><Plus size={14}/> Mark Attendance</button>
    }>
      {/* Controls */}
      <div className="filters-bar">
        <select className="form-control" style={{ width:120 }} value={month} onChange={e=>setMonth(parseInt(e.target.value))}>
          {months.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
        </select>
        <select className="form-control" style={{ width:100 }} value={year} onChange={e=>setYear(parseInt(e.target.value))}>
          {[2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      <div className="tabs">
        <div className={`tab${tab==='summary'?' active':''}`} onClick={()=>setTab('summary')}>Monthly Summary</div>
        <div className={`tab${tab==='detail'?' active':''}`} onClick={()=>setTab('detail')}>Daily Detail</div>
      </div>

      {tab==='summary' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : summary.length ? (
              <table>
                <thead><tr><th>Code</th><th>Name</th><th>Hub</th><th>Present</th><th>Absent</th><th>Leave</th><th>Half Day</th><th>Overtime</th></tr></thead>
                <tbody>
                  {summary.map(s=>(
                    <tr key={s.emp_id}>
                      <td><code style={{ fontSize:'.78rem' }}>{s.emp_code}</code></td>
                      <td>
                        <div style={{ fontWeight:500 }}>{s.name}</div>
                        <div className="text-sm text-muted">{s.designation}</div>
                      </td>
                      <td className="text-sm">{s.hub_name||'—'}</td>
                      <td style={{ color:'var(--success)', fontWeight:600 }}>{s.present_days||0}</td>
                      <td style={{ color:'var(--danger)' }}>{s.absent_days||0}</td>
                      <td style={{ color:'var(--info)' }}>{s.leave_days||0}</td>
                      <td>{s.half_days||0}</td>
                      <td>{s.overtime_hrs ? `${parseFloat(s.overtime_hrs).toFixed(1)} hrs` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="📅" title="No attendance records" />}
          </div>
        </div>
      )}

      {tab==='detail' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : records.length ? (
              <table>
                <thead><tr><th>Date</th><th>Employee</th><th>Check In</th><th>Check Out</th><th>Status</th><th>Source</th></tr></thead>
                <tbody>
                  {records.map(r=>(
                    <tr key={r.att_id}>
                      <td style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{fmtDate(r.date)}</td>
                      <td>
                        <div style={{ fontWeight:500 }}>{r.emp_name}</div>
                        <div className="text-sm text-muted">{r.emp_code}</div>
                      </td>
                      <td className="text-sm">{r.check_in ? new Date(r.check_in).toLocaleTimeString() : '—'}</td>
                      <td className="text-sm">{r.check_out ? new Date(r.check_out).toLocaleTimeString() : '—'}</td>
                      <td>
                        <span className={`badge ${r.status==='PRESENT'?'badge-success':r.status==='ABSENT'?'badge-danger':r.status==='LEAVE'?'badge-info':'badge-gray'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="text-sm">{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="📅" title="No records for selected period" />}
          </div>
        </div>
      )}

      <Modal open={showMark} onClose={()=>setShowMark(false)} title="Mark Attendance">
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Employee ID</label>
            <input className="form-control" placeholder="UUID or leave blank for self" value={markForm.empId} onChange={e=>setMarkForm(f=>({...f,empId:e.target.value}))} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date <span className="req">*</span></label>
              <input className="form-control" type="date" value={markForm.date} onChange={e=>setMarkForm(f=>({...f,date:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Status <span className="req">*</span></label>
              <select className="form-control" value={markForm.status} onChange={e=>setMarkForm(f=>({...f,status:e.target.value}))}>
                {['PRESENT','ABSENT','HALF_DAY','LEAVE','HOLIDAY'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Check In</label>
              <input className="form-control" type="time" value={markForm.checkIn} onChange={e=>setMarkForm(f=>({...f,checkIn:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Check Out</label>
              <input className="form-control" type="time" value={markForm.checkOut} onChange={e=>setMarkForm(f=>({...f,checkOut:e.target.value}))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Source</label>
            <select className="form-control" value={markForm.source} onChange={e=>setMarkForm(f=>({...f,source:e.target.value}))}>
              <option value="MANUAL">Manual</option>
              <option value="BIOMETRIC">Biometric</option>
              <option value="APP">App</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowMark(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleMark}>Save</button>
        </div>
      </Modal>
      <Modal open={showAttEdit} onClose={()=>{setShowAttEdit(false);setEditAtt(null);}} title="Edit Attendance Record">
        <div className="modal-body">
          <div style={{ padding:'10px 14px', background:'var(--gray-50)', borderRadius:8, marginBottom:12, fontSize:'.82rem' }}>
            <strong>{editAtt?.emp_name}</strong> · {editAtt?.date}
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-control" value={attForm.status} onChange={e=>setAttForm(f=>({...f,status:e.target.value}))}>
              {['PRESENT','ABSENT','HALF_DAY','LEAVE','HOLIDAY'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Check-in</label><input className="form-control" type="datetime-local" value={attForm.checkIn} onChange={e=>setAttForm(f=>({...f,checkIn:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Check-out</label><input className="form-control" type="datetime-local" value={attForm.checkOut} onChange={e=>setAttForm(f=>({...f,checkOut:e.target.value}))}/></div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><input className="form-control" value={attForm.notes} onChange={e=>setAttForm(f=>({...f,notes:e.target.value}))}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>{setShowAttEdit(false);setEditAtt(null);}}>Cancel</button>
          <button className="btn btn-primary" onClick={saveAttEdit}>Save Changes</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── Payroll ───────────────────────────────────────────────────────────────────
export function PayrollPage() {
  const [payroll, setPayroll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [month, setMonth]   = useState(new Date().getMonth()+1);
  const [year, setYear]     = useState(new Date().getFullYear());
  // const [selected, setSelected] = useState(null);
  const [showPayEdit, setShowPayEdit] = useState(false);
  const [editPayroll, setEditPayroll] = useState(null);
  const [payAdjust, setPayAdjust]   = useState({ basic:'', deductions:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll?month=${month}&year=${year}`).then(r=>r.data);
      if (res.success) setPayroll(res.data);
    } finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/payroll/generate', { month, year }).then(r=>r.data);
      if (res.success) { toast.success(`Generated ${res.data.filter(r=>r.status==='generated').length} payslips`); load(); }
    } catch { toast.error('Generation failed'); }
    finally { setGenerating(false); }
  };

  const finalize = async () => {
    if (!window.confirm(`Finalize payroll for ${month}/${year}? This cannot be undone.`)) return;
    try {
      await api.post('/payroll/finalize', { month, year }).then(r=>r.data);
      toast.success('Payroll finalized'); load();
    } catch { toast.error('Failed'); }
  };

  const printPayslip = (p) => {
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Payslip ${p.emp_code}</title>
      <style>body{font-family:Arial;font-size:12px;margin:30px} .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #eee} .total{font-weight:bold;font-size:14px;border-top:2px solid #1B2A4A;padding-top:8px}</style>
      </head><body>
      <h2 style="color:#1B2A4A">ECL — PAYSLIP</h2>
      <p><strong>${p.emp_name}</strong> (${p.emp_code}) | ${p.designation} | ${p.hub_name||''}</p>
      <p>Period: ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][p.month-1]} ${p.year}</p>
      <h4>Earnings</h4>
      <div class="row"><span>Basic Salary</span><span>PKR ${parseFloat(p.basic_salary).toLocaleString()}</span></div>
      <div class="row"><span>Earned Basic (${p.present_days} days)</span><span>PKR ${parseFloat(p.earned_basic).toLocaleString()}</span></div>
      <div class="row"><span>Overtime (${p.overtime_hrs}h)</span><span>PKR ${parseFloat(p.overtime_amount).toLocaleString()}</span></div>
      <div class="row total"><span>Gross</span><span>PKR ${parseFloat(p.gross_salary).toLocaleString()}</span></div>
      <h4>Deductions</h4>
      <div class="row"><span>EOBI</span><span>PKR ${parseFloat(p.deduction_eobi).toLocaleString()}</span></div>
      <div class="row"><span>Income Tax</span><span>PKR ${parseFloat(p.deduction_tax).toLocaleString()}</span></div>
      <div class="row total"><span>Net Salary</span><span style="color:#1B2A4A">PKR ${parseFloat(p.net_salary).toLocaleString()}</span></div>
      </body></html>`);
    w.print();
  };

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const totalNet = payroll.reduce((a,p)=>a+parseFloat(p.net_salary||0),0);
  const hasDraft = payroll.some(p=>p.status==='DRAFT');

  const savePayEdit = async () => {
    try {
      await api.patch('/hr/payroll/'+editPayroll.run_id, { basic:parseFloat(payAdjust.basic), deductions:parseFloat(payAdjust.deductions) });
      toast.success('Payroll updated'); setShowPayEdit(false); setEditPayroll(null); load();
    } catch(e){ toast.error(e.response?.data?.message||'Failed'); }
  };

  return (
    <AppShell title="Payroll" actions={
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-outline btn-sm" onClick={generate} disabled={generating}>
          {generating ? 'Generating…' : '⚙️ Generate'}
        </button>
        {hasDraft && <button className="btn btn-primary btn-sm" onClick={finalize}>✓ Finalize</button>}
      </div>
    }>
      <div className="filters-bar">
        <select className="form-control" style={{ width:120 }} value={month} onChange={e=>setMonth(parseInt(e.target.value))}>
          {months.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
        </select>
        <select className="form-control" style={{ width:100 }} value={year} onChange={e=>setYear(parseInt(e.target.value))}>
          {[2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      {payroll.length > 0 && (
        <div className="stat-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:20 }}>
          <div className="stat-card"><div className="stat-label">Employees</div><div className="stat-value">{payroll.length}</div></div>
          <div className="stat-card"><div className="stat-label">Total Net Payroll</div><div className="stat-value" style={{ fontSize:'1.1rem' }}>{fmtPKR(totalNet)}</div></div>
          <div className="stat-card"><div className="stat-label">Total EOBI</div><div className="stat-value" style={{ fontSize:'1.1rem' }}>{fmtPKR(payroll.reduce((a,p)=>a+parseFloat(p.deduction_eobi||0),0))}</div></div>
          <div className="stat-card"><div className="stat-label">Total Tax</div><div className="stat-value" style={{ fontSize:'1.1rem' }}>{fmtPKR(payroll.reduce((a,p)=>a+parseFloat(p.deduction_tax||0),0))}</div></div>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : payroll.length ? (
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Days</th><th>Gross</th><th>EOBI</th><th>Tax</th><th>Net Salary</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {payroll.map(p=>(
                  <tr key={p.payroll_id}>
                    <td><code style={{ fontSize:'.78rem' }}>{p.emp_code}</code></td>
                    <td>
                      <div style={{ fontWeight:500 }}>{p.emp_name}</div>
                      <div className="text-sm text-muted">{p.designation}</div>
                    </td>
                    <td>{p.present_days}</td>
                    <td>{fmtPKR(p.gross_salary)}</td>
                    <td className="text-sm">{fmtPKR(p.deduction_eobi)}</td>
                    <td className="text-sm">{fmtPKR(p.deduction_tax)}</td>
                    <td style={{ fontWeight:700, color:'var(--navy)' }}>{fmtPKR(p.net_salary)}</td>
                    <td><span className={`badge ${p.status==='FINALIZED'?'badge-success':p.status==='PAID'?'badge-navy':'badge-warning'}`}>{p.status}</span></td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={()=>printPayslip(p)}>🖨️ Slip</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div style={{ fontSize:40, marginBottom:12 }}>💰</div>
              <h3>No payroll for {months[month-1]} {year}</h3>
              <p>Click Generate to calculate payroll from attendance records.</p>
              <button className="btn btn-primary" style={{ marginTop:12 }} onClick={generate} disabled={generating}>
                {generating ? 'Generating…' : 'Generate Now'}
              </button>
            </div>
          )}
        </div>
      </div>
      <Modal open={showPayEdit} onClose={()=>{setShowPayEdit(false);setEditPayroll(null);}} title="Adjust Payroll Entry">
        <div className="modal-body">
          <div style={{ padding:'10px 14px', background:'var(--gray-50)', borderRadius:8, marginBottom:12, fontSize:'.85rem' }}>
            <strong>{editPayroll?.emp_name}</strong> · {editPayroll?.emp_code}
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Basic Salary (PKR)</label><input className="form-control" type="number" value={payAdjust.basic} onChange={e=>setPayAdjust(f=>({...f,basic:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Total Deductions (PKR)</label><input className="form-control" type="number" value={payAdjust.deductions} onChange={e=>setPayAdjust(f=>({...f,deductions:e.target.value}))}/></div>
          </div>
          <div style={{ marginTop:8, padding:'10px', background:'#EFF6FF', borderRadius:6, fontSize:'.82rem' }}>
            Net Pay: <strong style={{ color:'var(--success)' }}>PKR {(parseFloat(payAdjust.basic||0)-parseFloat(payAdjust.deductions||0)).toLocaleString()}</strong>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>{setShowPayEdit(false);setEditPayroll(null);}}>Cancel</button>
          <button className="btn btn-primary" onClick={savePayEdit}>Save Adjustment</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── Leave Management (M05 HR-FE-004) ──────────────────────────────────────────
export function LeaveManagementPage() {
  const { user }      = require('../../context/AuthContext').useAuth();
  const [data, setData]     = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showApply, setShowApply] = React.useState(false);
  const [allRequests, setAllRequests] = React.useState([]);
  const [form, setForm] = React.useState({ leaveType:'ANNUAL', fromDate:'', toDate:'', reason:'' });
  const [submitting, setSubmitting] = React.useState(false);
  const isHR = ['HR','ADMIN'].includes(user?.role);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const api = (await import('../../services/api')).default;
      if (isHR) {
        const [mine, all] = await Promise.all([
          api.get('/hr/leave/balances').then(r=>r.data),
          api.get('/hr/leave/requests').then(r=>r.data),
        ]);
        if (mine.success) setData(mine.data);
        if (all.success)  setAllRequests(all.data);
      } else {
        const r = await api.get('/hr/leave/balances').then(r=>r.data);
        if (r.success) setData(r.data);
        const req = await api.get('/hr/leave/requests').then(r=>r.data);
        if (req.success) setAllRequests(req.data);
      }
    } finally { setLoading(false); }
  }, [isHR]);

  React.useEffect(() => { load(); }, [load]);

  const applyLeave = async () => {
    if (!form.fromDate||!form.toDate) { toast.error('Select dates'); return; }
    setSubmitting(true);
    try {
      const api = (await import('../../services/api')).default;
      const r = await api.post('/hr/leave/apply', form).then(r=>r.data);
      if (r.success) { toast.success('Leave request submitted'); setShowApply(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSubmitting(false); }
  };

  const processLeave = async (requestId, status) => {
    try {
      const api = (await import('../../services/api')).default;
      await api.patch(`/hr/leave/requests/${requestId}`, { status });
      toast.success(`Leave ${status.toLowerCase()}`); load();
    } catch { toast.error('Failed'); }
  };

  const balances = Array.isArray(data) ? data : [];
  const LEAVE_TYPES = ['ANNUAL','SICK','CASUAL','MATERNITY','PATERNITY','UNPAID'];
  const SB = { PENDING:'badge-warning', APPROVED:'badge-success', REJECTED:'badge-danger', CANCELLED:'badge-gray' };

  return (
    <AppShell title="Leave Management" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowApply(true)}>+ Apply Leave</button>
    }>
      {/* Balance cards */}
      <div className="stat-grid" style={{ marginBottom:20 }}>
        {balances.map(b=>(
          <div key={b.balance_id} className="stat-card" style={{ textAlign:'center' }}>
            <div className="stat-label">{b.leave_type}</div>
            <div style={{ display:'flex', justifyContent:'center', gap:16, marginTop:6 }}>
              <div><div style={{ fontSize:'1.4rem', fontWeight:700, color:'var(--navy)' }}>{b.balance}</div><div style={{ fontSize:'.68rem', color:'var(--gray-400)' }}>Available</div></div>
              <div><div style={{ fontSize:'1.1rem', fontWeight:600, color:'var(--amber)' }}>{b.taken}</div><div style={{ fontSize:'.68rem', color:'var(--gray-400)' }}>Taken</div></div>
              <div><div style={{ fontSize:'1.1rem', color:'var(--gray-400)' }}>{b.entitled}</div><div style={{ fontSize:'.68rem', color:'var(--gray-400)' }}>Entitled</div></div>
            </div>
          </div>
        ))}
        {!balances.length && !loading && (
          <div className="stat-card" style={{ gridColumn:'1/-1', textAlign:'center', color:'var(--gray-400)', padding:20 }}>
            No leave balance data. Contact HR to set up your leave entitlements.
          </div>
        )}
      </div>

      {/* Requests table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">{isHR ? 'All Leave Requests' : 'My Leave Requests'}</span>
        </div>
        <div className="table-wrap">
          {loading ? <Spinner /> : allRequests.length ? (
            <table>
              <thead>
                <tr>
                  {isHR && <th>Employee</th>}
                  <th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th>
                  {isHR && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {allRequests.map(r=>(
                  <tr key={r.request_id}>
                    {isHR && <td><div style={{ fontWeight:500 }}>{r.emp_name}</div><div className="text-sm text-muted">{r.designation}</div></td>}
                    <td><span className="badge badge-info" style={{ fontSize:'.68rem' }}>{r.leave_type}</span></td>
                    <td className="text-sm">{r.from_date}</td>
                    <td className="text-sm">{r.to_date}</td>
                    <td style={{ fontWeight:600 }}>{r.days}</td>
                    <td style={{ maxWidth:180 }}><span className="truncate text-sm" style={{ display:'block' }}>{r.reason||'—'}</span></td>
                    <td><span className={`badge ${SB[r.status]||'badge-gray'}`}>{r.status}</span></td>
                    {isHR && r.status==='PENDING' && (
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-success btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>processLeave(r.request_id,'APPROVED')}>✓ Approve</button>
                          <button className="btn btn-danger btn-sm"  style={{ fontSize:'.7rem' }} onClick={()=>processLeave(r.request_id,'REJECTED')}>✗ Reject</button>
                        </div>
                      </td>
                    )}
                    {isHR && r.status!=='PENDING' && <td className="text-sm text-muted">{r.status}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="🏖️" title="No leave requests" />}
        </div>
      </div>

      {/* Apply modal */}
      <Modal open={showApply} onClose={()=>setShowApply(false)} title="Apply for Leave">
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor="lv-type">Leave Type <span className="req">*</span></label>
            <select id="lv-type" className="form-control" value={form.leaveType} onChange={e=>setForm(f=>({...f,leaveType:e.target.value}))}>
              {LEAVE_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="lv-from">From Date <span className="req">*</span></label>
              <input id="lv-from" className="form-control" type="date" value={form.fromDate} onChange={e=>setForm(f=>({...f,fromDate:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="lv-to">To Date <span className="req">*</span></label>
              <input id="lv-to" className="form-control" type="date" min={form.fromDate} value={form.toDate} onChange={e=>setForm(f=>({...f,toDate:e.target.value}))} />
            </div>
          </div>
          {form.fromDate && form.toDate && (
            <div className="alert alert-info" style={{ fontSize:'.82rem', padding:'8px 12px', marginBottom:8 }}>
              Duration: {Math.ceil((new Date(form.toDate)-new Date(form.fromDate))/86400000)+1} day(s)
            </div>
          )}
          <div className="form-group">
            <label className="form-label" htmlFor="lv-reason">Reason</label>
            <textarea id="lv-reason" className="form-control" rows={3} value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowApply(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={applyLeave} disabled={submitting} aria-busy={submitting}>
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </Modal>
    </AppShell>
  );
}
